import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import {API} from '../config/apis';
import { signIn, signOut } from '../config/googleSignin';
import i18n from 'i18next';

// Apple social login
export const appleLogin = createAsyncThunk(
  'appleLogin',
  async ({socialId, name, email}, {rejectWithValue}) => {
    try {
      const provider = 'apple';
      const {data: resp} = await axios.post(API.SOCIAL_LOGIN_API, {
        provider,
        socialId,
        email,
        name,
      }, {
        headers: {
          Language: i18n.language === 'dt' ? 'du' : 'en',
        },
      });

      const payloadData = resp?.data ?? resp?.Data;
      const success =
        resp?.status === true ||
        resp?.success === true ||
        resp?.Success === true ||
        Boolean(payloadData);
      const message = resp?.message || resp?.Message || 'Apple login failed';

      if (!success || !payloadData) {
        return rejectWithValue(message);
      }

      await AsyncStorage.setItem('Data', JSON.stringify(payloadData));
      return payloadData;
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.message ||
        'Apple login failed';
      return rejectWithValue(message);
    }
  },
);

export const logoutUser = createAsyncThunk(
  'logoutUser',
  async (_, {rejectWithValue}) => {
    try {
      await AsyncStorage.removeItem('Data');
      // Also end the Google session, so the next Google login shows the
      // account picker instead of silently reusing the old account.
      try {
        await signOut();
      } catch (e) {}
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const loginUser = createAsyncThunk(
  'loginUser',
  async ({email, password}, {rejectWithValue}) => {
    try {
      const response = await axios.post(API.LOGIN_API, {
        email,
        password,
      }, {
        headers: {
          Language: i18n.language === 'dt' ? 'du' : 'en',
        },
      });
      console.log(response);
      if(response.data.status===false){
        return rejectWithValue(response.data.message);
      }
      await AsyncStorage.setItem('Data', JSON.stringify(response.data.data));
      return response.data.data;
    } catch (err) {
      console.log('loginUser finally error',err);
      return rejectWithValue(err.response.data.message);
    }
  },
);

export const googleLogin = createAsyncThunk(
  'googleLogin',
  async (_, {rejectWithValue}) => {
    try{
      const payloadData = await signIn();
      await AsyncStorage.setItem('Data', JSON.stringify(payloadData));
      return payloadData;
    }catch(err){
      // Prefer the server's own reason over axios's "Request failed with
      // status code 4xx".
      const message =
        typeof err === 'string'
          ? err
          : err?.response?.data?.message ||
            err?.response?.data?.Message ||
            err?.message ||
            'Google Login Failed';
      return rejectWithValue(message);
    }
  },
);

export const alreadyLoggedIn = createAsyncThunk(
  'alreadyLoggedIn',
  async (_, {rejectWithValue}) => {
    const Data = await AsyncStorage.getItem('Data');
    if (!Data) {
      return rejectWithValue(false);
    }
    const parseData = JSON.parse(Data);
    if (!parseData || !parseData.token) {
      return rejectWithValue(false);
    }
    const loginDetails = {
      userId: parseData.userId,
      email: parseData.email,
      name: parseData.name,
      token: parseData.token,
    };
    return loginDetails;
  },
);

const loginSlice = createSlice({
  name: 'login',
  initialState: {
    isAuthenticated: false,
    userId: null,
    email: null,
    name: null,
    token: null,
    error: null,
    isLoading: false,
  },
  extraReducers: builder => {
    builder
      .addCase(logoutUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, state => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.userId = null;
        state.name = null;
        state.email = null;
        state.token = null;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to logout';
      })
      .addCase(loginUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        const {userId, email, name, token} = action.payload;
        state.isLoading = false;
        state.isAuthenticated = true;
        state.userId = userId;
        state.email = email;
        state.name = name;
        state.token = token;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to login';
      })
      .addCase(alreadyLoggedIn.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(alreadyLoggedIn.fulfilled, (state, action) => {
        const {userId, email, name, token} = action.payload;
        console.log(action.payload);
        state.isLoading = false;
        state.isAuthenticated = true;
        state.userId = userId;
        state.email = email;
        state.name = name;
        state.token = token;
        state.error = null;
      })
      .addCase(alreadyLoggedIn.rejected,(state,action)=>{
        state.isAuthenticated=action.payload;
        state.isLoading=false
      })
      .addCase(appleLogin.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(appleLogin.fulfilled, (state, action) => {
        const {userId, email, name, token} = action.payload;
        state.isLoading = false;
        state.isAuthenticated = true;
        state.userId = userId;
        state.email = email;
        state.name = name;
        state.token = token;
        state.error = null;
      })
      .addCase(appleLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to login with Apple';
      })
      .addCase(googleLogin.pending,(state)=>{
        state.isLoading = true;
        state.error = null;
      })
      .addCase(googleLogin.fulfilled,(state,action)=>{
        const {userId, email, name, token} = action.payload;
        state.isLoading = false;
        state.isAuthenticated = true;
        state.userId = userId;
        state.email = email;
        state.name = name;
        state.token = token;
        state.error = null;
      })
      .addCase(googleLogin.rejected,(state, action)=>{
        state.isLoading = false;
        state.error = action.payload || 'Failed to login';
      })
  },
});


export default loginSlice.reducer;
